import React, { useState, useRef, useEffect } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useParams, useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useChatMessages, useSendMessage, useDMRelays } from '@/hooks/useSendMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Send, ShieldCheck, MailWarning, ShieldX, Lock, Unlock, ShieldAlert } from 'lucide-react';

export function ChatConversationPage() {
  const { nip19: peerIdentifier } = useParams<{ nip19: string }>();
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  let peerPubkey = '';
  try {
    if (peerIdentifier) {
      const decoded = nip19.decode(peerIdentifier);
      if (decoded.type === 'npub') {
        peerPubkey = decoded.data as string;
      } else if (decoded.type === 'nprofile') {
        peerPubkey = (decoded.data as nip19.ProfilePointer).pubkey;
      }
    }
  } catch {
    // Falls out nicely
  }

  useSeoMeta({
    title: 'Direct Chat / Twittstr',
    description: 'Fully encrypted direct messages.',
  });

  if (!peerPubkey) {
    return (
      <div className="p-8 text-center bg-background flex flex-col gap-4 text-muted-foreground">
        <ShieldX className="size-10 mx-auto text-rose-500" />
        <p className="font-extrabold text-[15px]">Failed to parse counterpart user keys</p>
        <Button onClick={() => navigate('/chat')} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold w-fit mx-auto">
          Return to Inbox
        </Button>
      </div>
    );
  }

  const author = useAuthor(peerPubkey);
  const metadata = author.data?.metadata;

  const { data: routeInfo } = useDMRelays(peerPubkey);
  const { data: messages = [], isLoading: isMessagesLoading } = useChatMessages(peerPubkey);
  const sendMessageMut = useSendMessage(peerPubkey);

  const [inputText, setInputText] = useState('');
  const msgBottomRef = useRef<HTMLDivElement>(null);

  // Auto Scroll message threads bottom on entries
  useEffect(() => {
    msgBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!inputText.trim()) return;
    sendMessageMut.mutate({ content: inputText.trim() }, {
      onSuccess: () => {
        setInputText('');
      }
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSend();
  };

  const isFallbackNip04Active = routeInfo && routeInfo.source === 'none';

  return (
    <div className="flex flex-col w-full h-[calc(100vh-56px)] md:h-screen bg-background relative overflow-hidden">
      {/* Thread Header bar */}
      <header className="sticky top-0 bg-background/85 backdrop-blur-md z-30 border-b border-border flex items-center justify-between px-4 h-14 shrink-0">
        <div className="flex items-center gap-3 truncate">
          <button onClick={() => navigate('/chat')} className="p-2 hover:bg-secondary rounded-full transition-colors cursor-pointer mr-0.5">
            <ArrowLeft className="size-5" />
          </button>
          <img 
            src={metadata?.picture || `https://robohash.org/${peerPubkey}.png?set=set4`}
            alt="Recipient Avatar" 
            className="size-9 rounded-full object-cover border border-border bg-secondary shrink-0"
          />
          <div className="flex flex-col leading-tight truncate">
            <span className="font-extrabold text-[15px] truncate">{metadata?.display_name || metadata?.name || 'Anonymous'}</span>
            <span className="text-xs text-muted-foreground">@{nip19.npubEncode(peerPubkey).slice(0, 10)}...</span>
          </div>
        </div>

        {/* Client Protocol lock indicator */}
        <div className="flex items-center gap-2 pr-1">
          {isFallbackNip04Active ? (
            <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-500 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 rounded-full shrink-0">
              <Unlock className="size-3.5 shrink-0" />
              <span>NIP-04 Legacy</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-600 bg-teal-500/10 border border-teal-500/20 px-2.5 py-1 rounded-full shrink-0">
              <Lock className="size-3.5 shrink-0" />
              <span>NIP-17 Secure</span>
            </div>
          )}
        </div>
      </header>

      {/* Warning Bar (Damus Model: warn but still allow) */}
      {isFallbackNip04Active && (
        <div className="bg-amber-500/10 border-b border-amber-500/20 p-2.5 px-4 text-xs flex gap-2.5 items-center select-none text-amber-600 leading-normal">
          <ShieldAlert className="size-4 text-amber-500 shrink-0" />
          <span>
            <strong>Recipient has no DM relays (kind 10050) configured.</strong> Messaging falls back to traditional NIP-04 encryption which leaks metadata (sending and receiving pubkeys are readable by relays).
          </span>
        </div>
      )}

      {/* Message List viewport */}
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 bg-slate-500/[0.02]">
        {isMessagesLoading ? (
          Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className={`flex gap-3 max-w-[70%] ${idx % 2 === 0 ? 'self-end flex-row-reverse' : 'self-start'}`}>
              <Skeleton className="size-8 rounded-full" />
              <div className="space-y-1.5">
                <Skeleton className="h-9 w-40 rounded-[20px]" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))
        ) : messages.length > 0 ? (
          messages.map((msg) => {
            const isMe = msg.pubkey === user?.pubkey;
            return (
              <div 
                key={msg.id} 
                className={`flex flex-col gap-1 max-w-[75%] ${isMe ? 'self-end items-end' : 'self-start items-start'}`}
              >
                {/* Bubble */}
                <div 
                  className={`px-4 py-2 bg-secondary/80 rounded-[20px] text-[15px] leading-relaxed break-words relative truncate min-h-9 flex items-center
                    ${isMe ? 'bg-sky-500 text-white rounded-br-xs' : 'bg-muted/80 text-foreground rounded-bl-xs'}
                  `}
                >
                  <span>{msg.content}</span>
                </div>
                
                {/* Time Indicator & encryption flags */}
                <span className="text-[10px] text-muted-foreground mb-1">
                  {new Date(msg.created_at * 1000).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                  {msg.encryption === 'nip04' && <span className="ml-1 text-amber-500 font-semibold">(legacy ⚠️)</span>}
                  {msg.encryption === 'nip17' && <span className="ml-1 text-teal-600 font-semibold">(secure 🔒)</span>}
                </span>
              </div>
            );
          })
        ) : (
          <div className="m-auto text-center flex flex-col gap-2 p-6 max-w-sm">
            <Lock className="size-8 mx-auto text-muted-foreground mb-1" />
            <p className="font-extrabold">Decentralized Direct Space</p>
            <p className="text-xs text-muted-foreground leading-normal">
              Encrypting connection threads securely. Start the private conversation by typing your first message below!
            </p>
          </div>
        )}
        <div ref={msgBottomRef} />
      </div>

      {/* Message Composer Area */}
      <div className="p-3 border-t border-border shrink-0 bg-background flex gap-2 items-center">
        <Input 
          type="text" 
          placeholder="Start a new message" 
          className="flex-1 rounded-full h-11 bg-secondary/50 focus-visible:bg-background border-none focus-visible:ring-1 focus-visible:ring-sky-500 pl-4 pr-1 text-[15px]"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyPress}
          disabled={sendMessageMut.isPending}
          maxLength={1000}
        />
        <Button 
          onClick={handleSend}
          disabled={!inputText.trim() || sendMessageMut.isPending}
          size="icon" 
          className="rounded-full size-11 bg-sky-500 hover:bg-sky-600 text-white shrink-0 cursor-pointer"
        >
          <Send className="size-5" />
        </Button>
      </div>

    </div>
  );
}

export default ChatConversationPage;
